import { RunDetailFact, RunDetailLayout } from '@Components/Documents';
import { FrameNotFound } from '@Components/errorHandling';
import { type MessageVariant } from '@Components/Messages/messageVariant';
import { DOCUMENTS_API } from '@Constants/backendConstants';
import { isBeingWritten, useArchive } from '@Hooks/Documents';
import { type ApiArchive, EnumArchiveRunState } from '@Model';
import { Button, isOk, LoadingFrame, useTranslate } from '@synqo/frontend-component-library';
import { formatFileSize } from '@utils/formatFileSize';
import { parseApiMessages } from '@utils/parseApiMessages';
import { type ReactNode, useCallback, useMemo, useState } from 'react';
import { buildArchiveTimeline } from './archiveTimeline';

type Phase =
    | 'preparing'
    | 'hanging'
    | 'loading'
    | 'available'
    | 'ready'
    | 'expired'
    | 'unreachable'
    | 'failed'
    | 'runFailed'
    | 'rebuildFailed';

interface Step {
    key: string;
    phase: Phase;
    message?: string;
}

const VARIANTS: Record<Phase, MessageVariant> = {
    preparing: 'info',
    hanging: 'warning',
    loading: 'info',
    available: 'success',
    ready: 'success',
    expired: 'warning',
    unreachable: 'error',
    failed: 'error',
    runFailed: 'error',
    rebuildFailed: 'error',
};

export interface ArchiveDetailProps {
    archiveId: string;
}

/** One ZIP download: its verdict with the next step it allows, how far it got, what it holds and what it reported. */
// @ai-generated 2026-10-01T12:23:39+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5 (1M context)
export function ArchiveDetail({ archiveId }: ArchiveDetailProps) {
    const i18n = useTranslate('');
    const { info, download, rebuild } = useArchive(archiveId);
    const [step, setStep] = useState<Step>();
    const [busy, setBusy] = useState(false);

    const archive = info.data?.data ?? undefined;
    const key = `${archiveId}:${archive?.expires_at ?? ''}`;
    const timeline = useMemo(() => archive && buildArchiveTimeline(archive), [archive]);

    const fetchArchive = useCallback(async () => {
        setStep({ key, phase: 'loading' });
        setBusy(true);
        try {
            const outcome = await download();
            if (outcome.ok) {
                setStep({ key, phase: 'ready' });
                return;
            }

            setStep({ key, phase: outcome.gone ? 'expired' : 'failed', message: outcome.message });
        } catch {
            setStep({ key, phase: 'unreachable' });
        } finally {
            setBusy(false);
        }
    }, [key, download]);

    const requestRebuild = useCallback(async () => {
        setBusy(true);
        try {
            const result = await rebuild();
            const message = result?.messages[0]?.message;
            if (!isOk(result)) {
                setStep({ key, phase: 'rebuildFailed', message });
            }
        } catch {
            setStep({ key, phase: 'rebuildFailed' });
        } finally {
            setBusy(false);
        }
    }, [key, rebuild]);

    const retry = useCallback(() => (info.isError ? void info.refetch() : void fetchArchive()), [info, fetchArchive]);

    if (info.isPending) {
        return <LoadingFrame />;
    }

    if (!info.isError && archive === undefined) {
        return (
            <FrameNotFound
                apiResourcePath={`${DOCUMENTS_API}/archives`}
                primaryKey={archiveId}
                result={info.data ?? { data: undefined, messages: [] }}
                embedded={false}
            />
        );
    }

    const state = currentState(info.isError, archive, key, step);

    const verdicts: Record<Phase, { headline: string; detail?: string; action?: ReactNode }> = {
        preparing: {
            headline: i18n('Der Download wird erstellt.'),
            detail: i18n('Sie erhalten eine Benachrichtigung, sobald er bereit ist.'),
        },
        hanging: {
            headline: i18n('Der Download braucht ungewöhnlich lange.'),
            detail: i18n('Er kann mit derselben Auswahl neu erstellt werden.'),
            action: (
                <Button locked={busy} onClick={() => void requestRebuild()}>
                    {i18n('Download neu erstellen')}
                </Button>
            ),
        },
        loading: { headline: i18n('Der Download wird vorbereitet.') },
        available: {
            headline: i18n('Ihr Download ist bereit.'),
            action: (
                <Button locked={busy} onClick={() => void fetchArchive()}>
                    {i18n('Herunterladen')}
                </Button>
            ),
        },
        ready: {
            headline: i18n('Ihr Download ist bereit.'),
            detail: i18n('Der Download wurde gestartet.'),
            action: (
                <Button appearance="transparent" locked={busy} onClick={() => void fetchArchive()}>
                    {i18n('Erneut herunterladen')}
                </Button>
            ),
        },
        expired: {
            headline: state.message ?? i18n('Dieser Download ist abgelaufen.'),
            detail: i18n('Er kann mit derselben Auswahl neu erstellt werden.'),
            action: (
                <Button locked={busy} onClick={() => void requestRebuild()}>
                    {i18n('Download neu erstellen')}
                </Button>
            ),
        },
        unreachable: {
            headline: i18n('Der Download konnte nicht geladen werden.'),
            detail: i18n('Bitte prüfen Sie Ihre Verbindung.'),
            action: (
                <Button locked={busy} onClick={retry}>
                    {i18n('Erneut versuchen')}
                </Button>
            ),
        },
        failed: {
            headline: state.message ?? i18n('Der Download konnte nicht geladen werden.'),
            action: (
                <Button locked={busy} onClick={() => void fetchArchive()}>
                    {i18n('Erneut versuchen')}
                </Button>
            ),
        },
        runFailed: {
            headline: i18n('Der Download konnte nicht erstellt werden.'),
            detail: state.message,
            action: (
                <Button locked={busy} onClick={() => void requestRebuild()}>
                    {i18n('Download neu erstellen')}
                </Button>
            ),
        },
        rebuildFailed: {
            headline: state.message ?? i18n('Der Download konnte nicht neu erstellt werden.'),
            action: (
                <Button locked={busy} onClick={() => void requestRebuild()}>
                    {i18n('Download neu erstellen')}
                </Button>
            ),
        },
    };
    const verdict = verdicts[state.phase];

    return (
        <RunDetailLayout
            variant={VARIANTS[state.phase]}
            headline={verdict.headline}
            detail={verdict.detail}
            action={verdict.action}
            timeline={timeline}
            timelineLabel={i18n('Downloadstatus')}
            facts={
                archive && (
                    <>
                        <RunDetailFact label={i18n('Datei')} value={archive.file_name} />
                        {archive.state === EnumArchiveRunState.Finished && (
                            <>
                                <RunDetailFact label={i18n('Dokumente')} value={archive.document_count} />
                                <RunDetailFact label={i18n('Größe')} value={formatFileSize(archive.file_size)} />
                            </>
                        )}
                    </>
                )
            }
            result={archive ?? NO_RESULT}
            noMessages={i18n('Dieser Download hat keine Meldungen hinterlassen.')}
        />
    );
}

const NO_RESULT = { result_details: undefined };

// @ai-generated 2026-09-30T03:06:06+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5 (1M context)
function currentState(
    unreachable: boolean,
    archive: ApiArchive | undefined,
    key: string,
    step: Step | undefined,
): { phase: Phase; message?: string } {
    if (unreachable) {
        return { phase: 'unreachable' };
    }
    const runStarted = isBeingWritten(archive) && !archive?.can_rebuild;
    if (step?.key === key && step.phase === 'rebuildFailed' && !runStarted) {
        return step;
    }
    if (isBeingWritten(archive)) {
        return { phase: archive?.can_rebuild ? 'hanging' : 'preparing' };
    }
    if (archive?.state === EnumArchiveRunState.Failed) {
        return { phase: 'runFailed', message: firstMessage(archive.result_details) };
    }
    if (step?.key === key) {
        return step;
    }
    if (archive?.is_expired) {
        return { phase: 'expired' };
    }
    return { phase: 'available' };
}

// @ai-generated 2026-09-30T03:06:06+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5 (1M context)
function firstMessage(resultDetails: string | undefined): string | undefined {
    if (!resultDetails) {
        return undefined;
    }
    try {
        return parseApiMessages(resultDetails)[0]?.message;
    } catch {
        return undefined;
    }
}
